/**
 * React hook for fetching data from the backend API.
 *
 * Automatically acquires MSAL token and handles loading/error states.
 * Returns { data, loading, error } — falls back gracefully if API is unavailable.
 */

import { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { apiFetch } from "./client";

/**
 * @param {string|null} path - API path (e.g. "/api/apps/"). Pass null to skip.
 * @returns {{ data: any, loading: boolean, error: string|null }}
 */
export function useApi(path) {
  const { instance, inProgress, accounts } = useMsal();
  const [state, setState] = useState({
    data: null,
    loading: !!path,
    error: null,
  });

  useEffect(() => {
    if (!path) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    // Wait for MSAL to finish initializing / handling redirects
    if (inProgress !== InteractionStatus.None) return;

    // Wait for accounts to be restored from MSAL cache after refresh
    if (!accounts || accounts.length === 0) return;

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    apiFetch(instance, path)
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled)
          setState({ data: null, loading: false, error: err.message });
      });

    return () => {
      cancelled = true;
    };
  }, [path, instance, inProgress, accounts]);

  return state;
}
